const HTTPClient = require('../HTTPClient');

module.exports = class BaseModel {
  static models = {};

  static getById(id, { include = null, path = null } = {}) {
    let params = [];

    if (include != null) {
      if (include instanceof Array) { include = include.join(',') }
      params.push(`include=${include}`);
    }

    return new HTTPClient().get(`${path || this.basePath}/${id}?${params.join('&')}`).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static list({ filters = null, sort = null, page = null, pageSize = null, include = null, path = null } = {}) {
    let params = [];
    path ||= this.basePath;

    if (page != null) { params.push(`page=${page}`) }
    if (pageSize != null) { params.push(`page_size=${pageSize}`) }

    if (sort != null) {
      if (sort.constructor === Array) {
        params.push(`sort=${sort.join(',')}`);
      } else {
        params.push(`sort=${sort}`);
      }
    }

    if (filters != null) {
      params.push(...this._nestedParamBuilder(filters, 'filter'))
    }

    if (include != null) {
      if (include instanceof Array) { include = include.join(',') }
      params.push(`include=${include}`);
    }

    if (params.length > 0) {
      path = `${path}?${params.join('&')}`;
    }

    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItemArray.bind(this));
  }

  static create(item, { include = null, path = null } = {}) {
    let params = [];

    if (include != null) {
      if (include instanceof Array) { include = include.join(',') }
      params.push(`include=${include}`);
    }

    const parser = this.prototype.constructor._parseDataItem.bind(this);
    return new HTTPClient().post(`${path || this.basePath}?${params.join('&')}`, this._generateBody(item)).then(parser);
  }

  static update(item, { include = null, path = null } = {}) {
    let params = [];

    if (include != null) {
      if (include instanceof Array) { include = include.join(',') }
      params.push(`include=${include}`);
    }

    const parser = this.prototype.constructor._parseDataItem.bind(this);
    path = `${path || this.basePath}/${item.id || ''}?${params.join('&')}`;
    return new HTTPClient().patch(path, this._generateBody(item)).then(parser);
  }

  static delete(item, { path = null } = {}) {
    const parser = this.prototype.constructor._parseDataItem.bind(this);
    return new HTTPClient().delete(`${path || this.basePath}/${item.id || ''}`).then(parser);
  }

  static counts({ group = null, filters = null, path = null }) {
    let params = [];
    path = `${path || this.basePath}/counts`;

    if (group != null) { params.push(`group=${group}`) }

    if (filters != null) {
      params.push(...this._nestedParamBuilder(filters, 'filter'))
    }

    if (params.length > 0) {
      path = `${path}?${params.join('&')}`;
    }

    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static _parseDataItem(json) {
    if (json && json.meta && json.data) {
      json.data.meta = json.meta;
      json = json.data;
    }
    if (json && json.type) {
      const typ = this.models[json.type];
      if (typ) {
        return new typ(json);
      } else {
        console.warn(`Unable to parse platform type ${json.type} (type not defined)`);
        return json;
      }
    } else {
      // the object was null, or not a platform-defined object (e.g. plain string)
      return json;
    }
  }

  static parse(item) {
    return(item && BaseModel._parseDataItem(item))
  }

  static _parseDataItemArray(json) {
    const collection = [];
    let arr;
    if (json.hasOwnProperty('data') && json.hasOwnProperty('meta')) {
      arr = json.data;
      collection.meta = json.meta;
    } else {
      arr = json;
    }
    arr.forEach(element => {
      collection.push(this._parseDataItem(element));
    });
    return collection;
  }

  static _nestedParamBuilder(items, prefix = '') {
    if (items instanceof Array) {
      return (items.map(item => this._nestedParamBuilder(item, `${prefix}[]`)).flat());
    } else if (typeof items === 'object' && items !== null) {
      return (Object.keys(items).map(key => this._nestedParamBuilder(items[key], `${prefix}[${key}]`)).flat());
    } else if (items === null) {
      return ([`${prefix}=`]);
    } else if (items === undefined) {
      return ([]);
    } else {
      return ([`${prefix}=${items}`]);
    }
  }

  /*
    _generateBody converts an item into multipart where needed.

    It looks for values that are Files as part of the item. Files are relatively obvious
    in the browser, but in Node JS can be a bit fiddly due to the encoding and insistence
    on assuming that everything is a nice, simple string rather than binary.

    To build a file in node for upload from the local file system, use something like this:

    const raw = fs.readFileSync('/path/to/myobject.pdf', { encoding: 'binary', flag: 'r'})
    const ab = new Uint8Array(raw.length)
    for (let i=0; i< raw.length; i++) { ab[i] = raw[i].charCodeAt(0); }
    const file = new File([ab.buffer], 'myobject.pdf', { type: 'application/pdf' })
  */
  static _generateBody(item) {
    let body, attachments, processed;
    if (Array.isArray(item)) {
      attachments = {};
      processed = [];
      item.forEach((v, index) => {
        const [p, a] = this._generateBodyParts(v);
        processed.push(p);
        Object.keys(a).forEach((k) => attachments[`${index}.${k}`] = a[k]);
      });
    } else {
      [processed, attachments] = this._generateBodyParts(item);
    }
    if (Object.keys(attachments).length == 0) {
      return (processed);
    } else {
      body = new FormData();
      Object.keys(attachments).forEach(key => {
        body.append(key, attachments[key]);
      });

      body.append('data', JSON.stringify(processed));
      return (body);
    }
  }

  static _generateBodyParts(item) {
    let body;
    let attachments = {};
    Object.keys(item).forEach(key => {
      const value = item[key];
      if (value instanceof File) {
        attachments[key] = value;
        delete item[key];
      }
    });

    body = { [item.constructor.dataType || item.type]: item };

    return ([body, attachments]);
  }

  constructor(json = {}) {
    Object.assign(this, json);

    this.parseToDates(['createdAt', 'updatedAt']);
    this.type ||= this.constructor.dataType;
  }

  parseToDates(fields) {
    fields.forEach((field) => {
      if (field in this && this[field]) { this[field] = new Date(this[field]) }
    });
  }

  parseChildren(fields) {
    fields.forEach((field) => {
      if (field in this) {
        let value = this[field];
        if (value) {
          if (Array.isArray(value)) {
            this[field] = BaseModel._parseDataItemArray(value);
          } else {
            this[field] = BaseModel._parseDataItem(value);
          }
        }
      }
    });
  }

  get errors() { return this._errors }
  set errors(value) { this._errors = [] }

  isValid() {
    return (!this.errors || this.errors == []);
  }
}
